// src/hooks/useWorkOrderAPI.js
import { useState, useEffect, useCallback } from 'react';

// Datos de ejemplo mejorados
const initialWorkOrders = [
    {
        id: 'WO-2024-001',
        clientName: 'Transportes Mendoza SA',
        clientPhone: '+52 449 123 4567',
        clientEmail: 'contacto@transportesmendoza.com',
        vehicleType: 'Tractocamión',
        vehicleYear: '2022',
        vin: '1HGBH41JXMN109186',
        plates: 'ABC-123',
        model: 'Freightliner Cascadia',
        mileage: 152340,
        driverName: 'Juan Pérez',
        driverPhone: '+52 449 765 4321',
        driverDescription: 'Pierde potencia en subidas, humo negro en aceleración',
        problemDescription: 'Códigos P0201/P0202. Consumo alto de combustible. Falla en inyectores.',
        status: 'Pendiente',
        priority: 'Alta',
        createdDate: new Date('2024-10-13'),
        estimatedCompletion: new Date('2024-10-17'),
        estimatedCost: 25000,
        actualCost: 0,
        assignedTechnician: null,
        progress: 0,
        hasPhotos: false,
        hasNotes: true,
        partsOrdered: false,
        isRescue: true,
        isOutsideAgs: false,
        diagnosticType: 'Electrónico',
        partsUsed: [],
        photos: [],
        finalReport: '',
        serviceHistory: [],
        checklist: [
            { task: 'Diagnóstico electrónico', completed: false },
            { task: 'Revisión sistema inyección', completed: false },
            { task: 'Prueba de compresión', completed: false }
        ]
    },
    {
        id: 'WO-2024-002',
        clientName: 'Logística Norte SA de CV',
        clientPhone: '+52 449 234 5678',
        clientEmail: 'servicio@logisticanorte.com',
        vehicleType: 'Camión de Volteo',
        vehicleYear: '2020',
        vin: '2FTRX08L0YCA12345',
        plates: 'DEF-456',
        model: 'International HV Series',
        mileage: 98765,
        driverName: 'Carlos Rodríguez',
        driverPhone: '+52 449 876 5432',
        driverDescription: 'Fugas de aceite en motor',
        problemDescription: 'Fuga principal en sello de cárter. Nivel de aceite bajo constantemente.',
        status: 'En Progreso',
        priority: 'Media',
        createdDate: new Date('2024-10-12'),
        estimatedCompletion: new Date('2024-10-15'),
        estimatedCost: 8500,
        actualCost: 0,
        assignedTechnician: 'Técnico Juan García',
        progress: 40,
        hasPhotos: true,
        hasNotes: true,
        partsOrdered: true,
        isRescue: false,
        isOutsideAgs: true,
        diagnosticType: 'Mecánico',
        partsUsed: [
            { id: 'SELL-001', name: 'Sello de Cárter', quantity: 1, price: 1200 },
            { id: 'ACE-005', name: 'Aceite Motor 15W40', quantity: 8, price: 350 }
        ],
        photos: ['photo1.jpg', 'photo2.jpg'],
        finalReport: '',
        serviceHistory: [],
        checklist: [
            { task: 'Limpieza área de fuga', completed: true },
            { task: 'Reemplazo sello de cárter', completed: true },
            { task: 'Cambio de aceite', completed: false },
            { task: 'Prueba de presión', completed: false }
        ]
    }
];

const initialTechnicians = [
    { id: 'tech-1', name: 'Técnico Juan García', specialty: 'Mecánica General', workload: 3 },
    { id: 'tech-2', name: 'Técnico María López', specialty: 'Electrónica', workload: 2 },
    { id: 'tech-3', name: 'Técnico Pedro Martínez', specialty: 'Motor', workload: 1 }
];

const initialInventory = [
    { id: 'ECU-001', name: 'Módulo ECU', stock: 5, price: 15000, category: 'Electrónica' },
    { id: 'TEMP-205', name: 'Sensor Temperatura', stock: 12, price: 850, category: 'Sensores' },
    { id: 'FILT-100', name: 'Filtro Aceite', stock: 25, price: 450, category: 'Filtros' },
    { id: 'SELL-001', name: 'Sello de Cárter', stock: 8, price: 1200, category: 'Sellos' },
    { id: 'ACE-005', name: 'Aceite Motor 15W40', stock: 50, price: 350, category: 'Lubricantes' }
];

const initialTruckDatabase = {
    '1HGBH41JXMN109186': {
        plates: 'ABC-123',
        model: 'Freightliner Cascadia 2022',
        history: [
            { date: '2024-09-01', issue: 'Falla inyectores', repair: 'Reemplazo', cost: 18500 },
            { date: '2024-08-15', issue: 'Mantenimiento general', repair: 'Cambio de aceite y filtros', cost: 3200 },
        ],
        totalMaintenanceCost: 21700,
        lastService: '2024-09-01'
    },
    '2FTRX08L0YCA12345': {
        plates: 'DEF-456',
        model: 'International HV Series 2020',
        history: [
            { date: '2024-07-20', issue: 'Alineación y balanceo', repair: 'Ajuste completo', cost: 1800 },
        ],
        totalMaintenanceCost: 1800,
        lastService: '2024-07-20'
    }
};

export const useWorkOrderAPI = () => {
    const [workOrders, setWorkOrders] = useState(initialWorkOrders);
    const [technicians, setTechnicians] = useState(initialTechnicians);
    const [inventory, setInventory] = useState(initialInventory);
    const [truckDatabase, setTruckDatabase] = useState(initialTruckDatabase);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Simular carga de datos
    useEffect(() => {
        setIsLoading(true);
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    const refreshData = useCallback(async () => {
        setIsLoading(true);
        try {
            // Simular llamada a API
            await new Promise(resolve => setTimeout(resolve, 500));
            setError(null);
        } catch (err) {
            setError('Error al cargar los datos');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const createWorkOrder = useCallback(async (data) => {
        setIsLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 500));
            const id = `WO-${new Date().getFullYear()}-${String(workOrders.length + 1).padStart(3, '0')}`;
            const newOrder = {
                id,
                ...data,
                createdDate: new Date(),
                status: 'Pendiente',
                progress: 0,
                partsUsed: [],
                checklist: [],
                serviceHistory: [],
                actualCost: 0,
                hasPhotos: false,
                hasNotes: false,
                partsOrdered: false
            };
            setWorkOrders(prev => [...prev, newOrder]);
            return newOrder;
        } catch (err) {
            setError('Error al crear la orden');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [workOrders.length]);

    const updateWorkOrder = useCallback(async (id, updates) => {
        setIsLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 300));
            setWorkOrders(prev => prev.map(wo =>
                wo.id === id ? { ...wo, ...updates } : wo
            ));
        } catch (err) {
            setError('Error al actualizar la orden');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const deleteWorkOrder = useCallback(async (id) => {
        setIsLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 300));
            setWorkOrders(prev => prev.filter(wo => wo.id !== id));
        } catch (err) {
            setError('Error al eliminar la orden');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const bulkUpdateOrders = useCallback(async (orderIds, updates) => {
        setIsLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 500));
            setWorkOrders(prev => prev.map(wo =>
                orderIds.includes(wo.id) ? { ...wo, ...updates } : wo
            ));
        } catch (err) {
            setError('Error en actualización masiva');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        workOrders,
        setWorkOrders,
        technicians,
        clients: Array.from(new Set(workOrders.map(wo => wo.clientName))),
        inventory,
        truckDatabase,
        isLoading,
        error,
        refreshData,
        createWorkOrder,
        updateWorkOrder,
        deleteWorkOrder,
        bulkUpdateOrders,
        clearError
    };
};